import { useCallback, useMemo } from 'react'
import {
  Attributes,
  Experiment,
  useExperiment,
  useFeatureValue,
  useGrowthBook,
  WidenPrimitives,
} from '@growthbook/growthbook-react'
import { AppFeatures } from './app-features'
import { getExperimentValueFromQuery } from './utils'

const reconcileExperimentValue = <T extends keyof AppFeatures>(
  key: T,
  exposedValue: WidenPrimitives<AppFeatures[T]>,
) => {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const keyValue = urlParams?.get(key)
  const fromQuery =
    typeof keyValue === 'string' ? (getExperimentValueFromQuery(keyValue) as WidenPrimitives<AppFeatures[T]>) : null
  return fromQuery !== null ? fromQuery : exposedValue
}

export function useGBExperiment<T extends keyof AppFeatures>(key: T, defaultValue: AppFeatures[T]) {
  const growthbook = useGrowthBook()
  const exposedValue = useFeatureValue<AppFeatures[T]>(key, defaultValue)

  const value = useMemo(() => {
    return reconcileExperimentValue(key, exposedValue)
  }, [key, exposedValue])

  return [value, growthbook.ready] as const
}

export function useExperimentContext() {
  const growthbook = useGrowthBook()

  const resolvedSetAttributes = useCallback(
    (attributes: Attributes) => {
      growthbook.setAttributes({ ...growthbook.getAttributes(), ...attributes })
    },
    [growthbook],
  )

  const getFeatureValue = useCallback(
    <T extends keyof AppFeatures>(key: T, defaultValue: AppFeatures[T]) => {
      return reconcileExperimentValue(key, growthbook.getFeatureValue(key, defaultValue))
    },
    [growthbook],
  )

  return {
    getUserAttributes: growthbook.getAttributes,
    setUserAttributes: resolvedSetAttributes,
    getFeatureValue,
    ready: growthbook.ready,
  }
}

export function useInlineExperiment<T>(exp: Experiment<T>) {
  const { value } = useExperiment(exp)

  return value
}
